import { Grid, Grow, Stack, Typography } from "@mui/material";
import { type FC } from "react";
import { useTranslation } from "react-i18next";
import type { ISO8601_DATE } from "../../../../utils/iso8601";
import { ScheduleImmediateButton } from "../../../schedule/components/buttons/schedule-immediate-button";
import { ScheduleSpecificButton } from "../../../schedule/components/buttons/schedule-specific-button";
import { SpecificDateTimeSelect } from "../../../schedule/components/specific-date-time-select";
import type { SetSchedulePayload } from "../../reducer/actions";
import type { FollowUpSchemaType } from "../../reducer/schema";

interface FollowUpScheduleFinishedProps {
  value: Extract<FollowUpSchemaType, { mode: "finished" }>["schedule"];
  onChange: (val: Extract<SetSchedulePayload, { mode: "finished" }>["schedule"]) => void;
}

export const FollowUpScheduleFinished: FC<FollowUpScheduleFinishedProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Stack direction="column" spacing={2}>
      <Grid container direction="row" spacing={2} justifyContent="space-between">
        <Grid size={{ xs: 12, sm: 6 }}>
          <ScheduleImmediateButton
            onClick={() => onChange({ type: "immediate" })}
            selected={value.type === "immediate"}
            subtitle={t("The Follow-Up will send as soon as it's published")}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ScheduleSpecificButton
            onClick={() =>
              onChange({
                type: "specific",
                date: null,
                time: null,
                timezone: null,
              })
            }
            selected={value.type === "specific"}
            subtitle={t("Set a date and time in the future to send the Follow-Up")}
            sx={{ width: "100%" }}
          />
        </Grid>
      </Grid>
      {value.type === "specific" && (
        <Grow in={value.type === "specific"}>
          <Stack direction="column" spacing={2}>
            <Typography variant="body1">{t("Select a date and time to send")}</Typography>
            <SpecificDateTimeSelect
              date={{
                value: value.date as ISO8601_DATE | null,
                onChange: (val) => onChange({ ...value, date: val }),
              }}
              time={{
                value: value.time,
                onChange: (val) => onChange({ ...value, time: val }),
              }}
              timezone={{
                value: value.timezone,
                onChange: (val) => onChange({ ...value, timezone: val }),
              }}
            />
          </Stack>
        </Grow>
      )}
    </Stack>
  );
};
